"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pydanticAiProvider = void 0;
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const tool_definitions_1 = require("../../../core/normalize/tool-definitions");
function isPydanticMessage(value) {
    const message = (0, json_1.asRecord)(value);
    return ((message?.kind === "request" || message?.kind === "response") &&
        Array.isArray(message.parts));
}
// tryUnwrapMessage returns one message; split mixed-role Pydantic request parts in the claim instead.
function pydanticAiMessage(message) {
    if (message.kind === "response") {
        return [{ role: "assistant", parts: message.parts }];
    }
    return message.parts.map((part) => {
        const kind = (0, json_1.asRecord)(part)?.part_kind;
        let role = "user";
        if (kind === "system-prompt")
            role = "system";
        if (kind === "tool-return")
            role = "tool";
        // Prompt content may itself contain several multimodal parts.
        if (kind === "system-prompt" || kind === "user-prompt") {
            return { role, content: (0, json_1.asRecord)(part)?.content };
        }
        return { role, parts: [part] };
    });
}
/** Extract native history without claiming unrelated application state. */
function pydanticAiMessages(root, kind) {
    const history = (0, json_1.parseArray)((0, json_1.parseRecord)(root._state)?.message_history);
    if (!history?.length || !history.every(isPydanticMessage))
        return [];
    (0, json_1.recordKeyAsParsed)(root, "_state", "message_history");
    return [
        {
            kind: "sequence",
            values: history.flatMap(pydanticAiMessage),
            fallbackRole: kind === "input" ? "user" : "assistant",
        },
    ];
}
function pydanticAiPart(part, context) {
    const { part_kind, tool_name, tool_call_id, args, ...rest } = part;
    let value;
    switch (part_kind) {
        case "text":
            value = { ...rest, type: "text" };
            break;
        case "tool-call":
            value = {
                ...rest,
                type: "tool-call",
                toolName: tool_name,
                toolCallId: tool_call_id,
                input: args,
            };
            break;
        case "tool-return":
            value = {
                ...rest,
                type: "tool-result",
                toolName: tool_name,
                toolCallId: tool_call_id,
                output: part.content,
            };
            break;
        default:
            return __1.unmatched;
    }
    return (0, __1.claimed)(context.normalizePartValue(value)[0] ?? null);
}
/** Builtin tools use `kind` where the common tool parser expects `type`. */
function pydanticAiBuiltinTool(value) {
    const tool = (0, json_1.asRecord)(value);
    if (!tool || typeof tool.kind !== "string")
        return value;
    const { kind, ...rest } = tool;
    return { ...rest, type: kind };
}
function pydanticAiToolDefinitionSources(carrier) {
    const state = (0, json_1.parseRecord)(carrier.root?._state);
    const sources = [];
    for (const [sourceKey, value] of [
        [
            "model_request_parameters",
            carrier.metadataAttributes?.model_request_parameters,
        ],
        [
            "_state.last_model_request_parameters",
            state?.last_model_request_parameters,
        ],
    ]) {
        const parameters = (0, json_1.parseRecord)(value);
        for (const key of ["function_tools", "output_tools", "builtin_tools"]) {
            if (parameters?.[key] === undefined)
                continue;
            sources.push({
                sourceKey: `${sourceKey}.${key}`,
                value: key === "builtin_tools"
                    ? ((0, json_1.parseArray)(parameters[key]) ?? []).map(pydanticAiBuiltinTool)
                    : parameters[key],
                options: {
                    allowProviderToolWithoutName: key === "builtin_tools",
                    allowToolMap: key !== "builtin_tools",
                },
            });
        }
    }
    return sources;
}
exports.pydanticAiProvider = {
    name: "pydantic-ai",
    claimMessages: pydanticAiMessages,
    tryNormalizeUntypedPart: pydanticAiPart,
    // Pydantic AI tool declarations: { name, description,
    // parameters_json_schema }.
    tryNormalizeToolDefinition: (value) => {
        if (value.parameters_json_schema === undefined)
            return __1.unmatched;
        const definition = (0, tool_definitions_1.toolDefinition)({
            name: value.name,
            description: value.description,
            inputSchema: value.parameters_json_schema,
            type: value.type,
            providerMetadata: (0, tool_definitions_1.toolDefinitionProviderMetadata)(value, value),
        });
        return definition ? (0, __1.claimed)(definition) : __1.unmatched;
    },
    collectToolDefinitionSources: pydanticAiToolDefinitionSources,
};
